import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowLeft, Share2, Copy, Check, QrCode, ShieldCheck,
  ScanLine, FileSignature, Ban, HelpCircle, KeyRound,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/docsite-verified-rx';
const SHARE_MESSAGE = (
  `DocSite Verified Rx is now live.\n\n` +
  `Drop any DocSite prescription PDF at docsite.care/rx-verify and see instantly whether it is genuine, revoked, or unknown. Cryptographic SHA-256 fingerprint check — no account, no login, no paperwork.\n\n` +
  `Every prescription now carries a scannable QR code that lands on this verification page directly.\n\n` +
  `Read more: ${SHARE_URL}`
);

const FEATURES = [
  { icon: ScanLine, title: 'One drop, instant verdict', body: 'Any pharmacist, insurer, or patient can drop a DocSite Rx PDF at docsite.care/rx-verify. Within a second we return VERIFIED, REVOKED, or NOT GENUINE — with the doctor identity and issue date.', color: 'sky' },
  { icon: QrCode, title: 'A QR code on every prescription', body: 'Every DocSite prescription now prints a QR code that resolves directly to the verification page for that specific Rx. Scan-and-go — no typing an ID, no visiting a website.', color: 'emerald' },
  { icon: ShieldCheck, title: 'SHA-256 cryptographic proof', body: 'We fingerprint every prescription with SHA-256 at issue time. Any tampering — even a single character — breaks the fingerprint. Same evidentiary weight as Aadhaar e-Sign or DigiLocker verification.', color: 'violet' },
  { icon: Ban, title: 'Revocation aware', body: 'If a doctor revokes a prescription (e.g. wrong dose, patient reported adverse reaction), the verify page shows REVOKED in amber with the doctor-provided reason. Pharmacists know instantly.', color: 'amber' },
  { icon: HelpCircle, title: 'Fraud protection built-in', body: 'A prescription that was never issued by DocSite returns NOT GENUINE in red. Any forged or altered document is caught at the pharmacy counter, not months later at insurance audit.', color: 'rose' },
  { icon: KeyRound, title: 'Zero storage, forever verifiable', body: 'We do not need to keep the PDF blob on our servers to verify it — the SHA-256 fingerprint is enough. This is the same model Aadhaar e-Sign and blockchain notaries use.', color: 'sky' },
];

const COLOR_MAP = {
  sky:     { bg: 'bg-sky-100',     border: 'border-sky-200',     icon: 'text-sky-700' },
  emerald: { bg: 'bg-emerald-100', border: 'border-emerald-200', icon: 'text-emerald-700' },
  violet:  { bg: 'bg-violet-100',  border: 'border-violet-200',  icon: 'text-violet-700' },
  amber:   { bg: 'bg-amber-100',   border: 'border-amber-200',   icon: 'text-amber-700' },
  rose:    { bg: 'bg-rose-100',    border: 'border-rose-200',    icon: 'text-rose-700' },
};

function FeatureCard({ f }) {
  const c = COLOR_MAP[f.color] || COLOR_MAP.sky;
  const Ico = f.icon;
  return (
    <div className={`rounded-2xl bg-white border ${c.border} p-5 flex gap-4 items-start hover:shadow-sm transition-shadow`}>
      <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center shrink-0`}>
        <Ico size={20} className={c.icon} strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-slate-900 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{f.body}</p>
      </div>
    </div>
  );
}

export default function VerifiedRxLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="verified-rx-launch-page">
      <Helmet>
        <title>DocSite Verified Rx — public prescription verification | DocSite</title>
        <meta name="description" content="Drop any DocSite prescription PDF to instantly verify authenticity. Cryptographic SHA-256 fingerprint check, no account required." />
        <meta property="og:title" content="DocSite Verified Rx" />
        <meta property="og:description" content="Any pharmacist, insurer or patient can verify a DocSite prescription in one scan." />
        <meta property="og:url" content={SHARE_URL} />
      </Helmet>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <Link to="/whats-new" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-6" data-testid="back-to-whats-new">
          <ArrowLeft size={12} /> All updates
        </Link>

        <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-200 p-6 sm:p-10 mb-6">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-600 text-white">
            <Sparkles size={10} /> Just Launched · 4 Aug 2026
          </span>
          <div className="mt-5 flex items-center gap-4">
            <span className="inline-flex w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-emerald-600 via-sky-500 to-violet-500 shadow-lg items-center justify-center">
              <ShieldCheck size={40} className="text-white" strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700/80">Public Verify Tool · What&apos;s new</p>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Public · QR-scannable · Cryptographic proof · Zero storage</p>
            </div>
          </div>
          <h1 className="text-2xl sm:text-4xl font-semibold text-slate-900 mt-5 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Any pharmacist, insurer, or patient can now verify a DocSite prescription in one scan.
          </h1>
          <p className="mt-3 text-base sm:text-lg text-slate-700 leading-relaxed">
            Drop a Rx PDF, get an instant verdict. No account. No login. No paperwork. Every prescription
            now prints a QR code that lands on the verification page for that specific Rx.
          </p>
          <Link to="/rx-verify" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold" data-testid="try-verify-cta">
            Try the verify tool <ScanLine size={14} />
          </Link>
        </div>

        <div className="space-y-3">
          {FEATURES.map((f, idx) => (<div key={idx} data-testid={`feature-${idx}`}><FeatureCard f={f} /></div>))}
        </div>

        <div className="mt-8 rounded-3xl bg-slate-900 text-white p-6 sm:p-8">
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">Spread the word</p>
          <h3 className="text-xl sm:text-2xl font-semibold mt-2 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Send this to the pharmacist you deal with most often.
          </h3>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-sm font-bold" data-testid="share-whatsapp">
              <Share2 size={14} /> Share on WhatsApp
            </a>
            <button onClick={copyLink}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold border border-slate-700" data-testid="share-copy">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Link copied' : 'Copy link'}
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-10">Published 4 August 2026 · DocSite Verified Rx v1</p>
      </div>
    </div>
  );
}
